// Hook for easy audit logging in React components
import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import AuditService from '../services/auditService'
import AnalyticsService from '../services/analyticsService'

export const useAudit = () => {
  const { user } = useAuth()

  // Log premium feature access
  const logPremiumAccess = useCallback(async (feature: string, details?: any) => {
    if (!user?.id) return

    await AnalyticsService.trackPremiumAccess(user.id, feature, details)
  }, [user?.id])

  // Log parking booking
  const logParkingBooking = useCallback(async (spotDetails: any) => {
    if (!user?.id) return

    await AnalyticsService.trackParkingBooking(user.id, spotDetails)
  }, [user?.id])

  // Log ticket download
  const logTicketDownload = useCallback(async (ticketDetails: any) => {
    if (!user?.id) return

    await AnalyticsService.trackTicketDownload(user.id, ticketDetails)
  }, [user?.id])

  // Log arrival confirmation
  const logArrivalConfirmation = useCallback(async (spotDetails: any) => {
    if (!user?.id) return

    await AnalyticsService.trackArrivalConfirmation(user.id, spotDetails)
  }, [user?.id])

  // Log dashboard access
  const logDashboardAccess = useCallback(async () => {
    if (!user?.id) return

    await AnalyticsService.trackDashboardAccess(user.id)
  }, [user?.id])

  // Log premium map usage
  const logPremiumMapUsage = useCallback(async (action: string, details?: any) => {
    if (!user?.id) return

    await AnalyticsService.trackPremiumMapUsage(user.id, action, details)
  }, [user?.id])

  // Log checkout snapshot
  const logCheckoutSnapshot = useCallback(async (checkoutHtml: string, termsAccepted: boolean) => {
    if (!user?.id) return

    await AuditService.logCheckoutSnapshot({
      userId: user.id,
      checkoutHtml,
      termsAccepted
    })
  }, [user?.id])

  // Log device correlation (for chargeback analysis)
  const logDeviceCorrelation = useCallback(async (paymentId?: string, paymentIp?: string, paymentUserAgent?: string) => {
    if (!user?.id) return

    await AuditService.createDeviceCorrelation({
      userId: user.id,
      paymentId,
      paymentIp,
      paymentUserAgent
    })
  }, [user?.id])

  // Get complete audit trail for user
  const getAuditTrail = useCallback(async () => {
    if (!user?.id) return null

    return await AuditService.getAuditTrail(user.id)
  }, [user?.id])

  // Log errors with detailed context for debugging (requires authenticated user)
  const logError = useCallback(async (
    errorType: string,
    error: Error | unknown,
    additionalContext?: any
  ) => {
    if (!user?.id) {
      console.warn('⚠️ Cannot log error: No user logged in')
      return
    }

    try {
      const errorDetails = {
        errorType,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
        url: window.location.href,

        // Enriched context
        context: {
          ...additionalContext,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          browser: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            online: navigator.onLine,
            cookiesEnabled: navigator.cookieEnabled
          },
          performance: {
            memory: (performance as any).memory ? {
              usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
              totalJSHeapSize: (performance as any).memory.totalJSHeapSize
            } : undefined,
            timing: performance.timing ? {
              loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
              domReady: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart
            } : undefined
          }
        }
      }

      await AuditService.logUserActivity({
        userId: user.id,
        eventType: `ERROR_${errorType}`,
        details: errorDetails
      })

      console.log(`📊 Error logged: ${errorType}`, errorDetails)
    } catch (logError) {
      console.error('❌ Failed to log error:', logError)
    }
  }, [user?.id])

  // Log system errors that work even without authenticated user
  const logSystemError = useCallback(async (
    errorType: string,
    error: Error | unknown,
    context?: any
  ) => {
    const errorData = {
      errorType,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      context,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: user?.id || null,
      isAuthenticated: !!user?.id,
      sessionId: AuditService.getSessionId()
    }

    // Always log to console
    console.error(`🚨 SYSTEM ERROR [${errorType}]:`, errorData)

    try {
      // Direct Supabase insert that works even without user
      const { error: supabaseError } = await supabase
        .from('user_activity_logs')
        .insert({
          user_id: user?.id || null,
          event_type: `ERROR_${errorType}`,
          details: errorData,
          session_id: AuditService.getSessionId(),
          ip_address: null, // Will be filled by Supabase
          user_agent: navigator.userAgent
        })

      if (supabaseError) {
        console.error('❌ Failed to save system error to DB:', supabaseError)
      } else {
        console.log('✅ System error logged to database')
      }
    } catch (dbError) {
      console.error('❌ Database logging failed:', dbError)
    }
  }, [user?.id])

  // Test function to verify audit logging works
  const testAuditLogging = useCallback(async () => {
    if (!user?.id) {
      console.warn('❌ AUDIT TEST: No user logged in')
      return false
    }

    try {
      console.log('🧪 AUDIT TEST: Starting audit logging test...')

      await AuditService.logUserActivity({
        userId: user.id,
        eventType: 'TEST_AUDIT_LOG',
        details: { test: true, timestamp: new Date().toISOString() }
      })

      console.log('✅ AUDIT TEST: Test log inserted successfully')
      return true
    } catch (error) {
      console.error('❌ AUDIT TEST: Failed to insert test log:', error)
      return false
    }
  }, [user?.id])

  return {
    logPremiumAccess,
    logParkingBooking,
    logTicketDownload,
    logArrivalConfirmation,
    logDashboardAccess,
    logPremiumMapUsage,
    logCheckoutSnapshot,
    logDeviceCorrelation,
    getAuditTrail,
    logError,
    logSystemError,
    testAuditLogging
  }
}

export default useAudit
